/**
 * Fixture data for GovernedImprovementPanel integration
 * 
 * Deterministic fixture data for testing the improvement panel in the Governance Workspace.
 * This data is read-only and represents a typical improvement workflow state.
 */

import {
  ImprovementIntentOutput,
  RatchetRecord,
  LineageIndex,
  NextStepProposal,
  ThreadStateTransition,
  ResumeOrchestration,
  CanonicalThreadSummaryExport,
} from '../../modules/improvementIntentRouter/types';
import { GovernedImprovementCanonicalThreadDashboard, type CanonicalThread } from '../../modules/improvementIntentRouter/ui/GovernedImprovementCanonicalThreadDashboard';
import { GovernedImprovementThreadStateTransitionPanel } from '../../modules/improvementIntentRouter/ui/GovernedImprovementThreadStateTransitionPanel';
import { ArtifactIndex } from '../../modules/improvementIntentRouter/ui/GovernedImprovementArtifactIndexPanel';
import { LIVE_ARTIFACT_INDEX } from './liveArtifactIndex';

export const fixtureIntentOutput: ImprovementIntentOutput = {
  intentClass: 'ui_refinement',
  canonicalThreadHint: 'UI refinement thread',
  prerequisiteDetected: false,
  recommendedRatchet: 'UI_REFINEMENT_V1',
  resumeTarget: 'ui_refinement',
  allowedNextActions: ['implement_ui_changes', 'test_ui_changes', 'request_review'],
  blockedActions: ['deploy_to_production', 'skip_local_testing'],
  reasonCodes: ['ui_refinement_pattern'],
  executionAuthority: false,
};

export const fixtureRatchetRecord: RatchetRecord = {
  recordVersion: 'v1',
  ratchetId: 'fixture-ratchet-id-12345',
  intentClass: 'ui_refinement',
  canonicalThreadId: 'fixture-thread-id-67890',
  canonicalThreadHint: 'UI refinement thread',
  status: 'proposed',
  prerequisiteRatchet: undefined,
  resumeTarget: 'ui_refinement',
  updates: [],
  supersedes: [],
  forksFrom: [],
  dependsOn: [],
  artifactRefs: ['artifacts/ui-refinement-v1.json'],
  decisionSummary: 'Implement UI refinement improvements for governance workspace',
  proofOnlySafeSummary: 'UI refinement ratchet proposed',
  privateNotesPointer: undefined,
  nextRecommendedRatchet: 'UI_REFINEMENT_V1',
  executionAuthority: false,
  createdAt: '2026-05-16T23:00:00.000Z',
  recordHash: 'fixture-record-hash-abc123',
  isValid: true,
  validationReasonCodes: [],
};

export const fixtureLineageIndex: LineageIndex = {
  indexVersion: 'v1',
  canonicalThreads: {
    'fixture-thread-id-67890': {
      threadId: 'fixture-thread-id-67890',
      threadHint: 'UI refinement thread',
      ratchetIds: ['fixture-ratchet-id-12345'],
      activeRatchetIds: ['fixture-ratchet-id-12345'],
      completedRatchetIds: [],
    },
  },
  activeRatchets: ['fixture-ratchet-id-12345'],
  blockedRatchets: [],
  supersededRatchets: [],
  archivedRatchets: [],
  dependencyEdges: [],
  resumeTargets: [],
  duplicateRiskWarnings: [],
  indexHash: 'fixture-index-hash-def456',
};

export const fixtureNextStepProposal: NextStepProposal = {
  proposalVersion: 'v1',
  recommendedRatchet: 'UI_REFINEMENT_V1',
  canonicalThreadId: 'fixture-thread-id-67890',
  proposalReason: 'Continue with active ratchet',
  prerequisiteRequired: false,
  duplicateRiskWarnings: [],
  allowedNextActions: ['proceed_with_ratchet', 'review_progress'],
  blockedActions: [],
  executionAuthority: false,
  proposalHash: 'fixture-proposal-hash-ghi789',
};

export const fixtureWithPrerequisite: {
  intentOutput: ImprovementIntentOutput;
  ratchetRecord: RatchetRecord;
  lineageIndex: LineageIndex;
  nextStepProposal: NextStepProposal;
} = {
  intentOutput: {
    intentClass: 'blocked_by_prerequisite',
    canonicalThreadHint: 'Complete local dev hardening before UI refinement',
    prerequisiteDetected: true,
    recommendedRatchet: 'LOCAL_DEV_HARDENING_V1',
    resumeTarget: 'ui_refinement',
    allowedNextActions: ['complete_prerequisite', 'resume_after_prerequisite'],
    blockedActions: ['proceed_without_prerequisite'],
    reasonCodes: ['blocked_by_prerequisite', 'prerequisite_ratchet_detected'],
    executionAuthority: false,
  },
  ratchetRecord: {
    recordVersion: 'v1',
    ratchetId: 'fixture-ratchet-id-blocked',
    intentClass: 'blocked_by_prerequisite',
    canonicalThreadId: 'fixture-thread-id-blocked',
    canonicalThreadHint: 'Complete local dev hardening before UI refinement',
    status: 'blocked',
    prerequisiteRatchet: 'LOCAL_DEV_HARDENING_V1',
    resumeTarget: 'ui_refinement',
    updates: [],
    supersedes: [],
    forksFrom: [],
    dependsOn: [],
    artifactRefs: [],
    decisionSummary: 'Blocked by prerequisite',
    proofOnlySafeSummary: 'Prerequisite required',
    privateNotesPointer: undefined,
    nextRecommendedRatchet: 'LOCAL_DEV_HARDENING_V1',
    executionAuthority: false,
    createdAt: '2026-05-16T23:00:00.000Z',
    recordHash: 'fixture-record-hash-blocked',
    isValid: true,
    validationReasonCodes: [],
  },
  lineageIndex: {
    indexVersion: 'v1',
    canonicalThreads: {
      'fixture-thread-id-blocked': {
        threadId: 'fixture-thread-id-blocked',
        threadHint: 'Complete local dev hardening before UI refinement',
        ratchetIds: ['fixture-ratchet-id-blocked'],
        activeRatchetIds: [],
        completedRatchetIds: [],
      },
    },
    activeRatchets: [],
    blockedRatchets: ['fixture-ratchet-id-blocked'],
    supersededRatchets: [],
    archivedRatchets: [],
    dependencyEdges: [],
    resumeTargets: [
      {
        blockedRatchetId: 'fixture-ratchet-id-blocked',
        prerequisiteRatchetId: 'LOCAL_DEV_HARDENING_V1',
        resumeTarget: 'ui_refinement',
      },
    ],
    duplicateRiskWarnings: [],
    indexHash: 'fixture-index-hash-blocked',
  },
  nextStepProposal: {
    proposalVersion: 'v1',
    recommendedRatchet: 'LOCAL_DEV_HARDENING_V1',
    canonicalThreadId: 'fixture-thread-id-blocked',
    proposalReason: 'Blocked ratchet requires prerequisite: LOCAL_DEV_HARDENING_V1',
    prerequisiteRequired: true,
    resumeTarget: 'ui_refinement',
    duplicateRiskWarnings: [],
    allowedNextActions: ['complete_prerequisite', 'review_blockage'],
    blockedActions: ['proceed_without_prerequisite'],
    executionAuthority: false,
    proposalHash: 'fixture-proposal-hash-blocked',
  },
};

export const fixtureWithDuplicateWarnings: {
  intentOutput: ImprovementIntentOutput;
  ratchetRecord: RatchetRecord;
  lineageIndex: LineageIndex;
  nextStepProposal: NextStepProposal;
} = {
  intentOutput: {
    intentClass: 'ui_refinement',
    canonicalThreadHint: 'UI refinement thread',
    prerequisiteDetected: false,
    recommendedRatchet: 'UI_REFINEMENT_V1',
    resumeTarget: 'ui_refinement',
    allowedNextActions: ['implement_ui_changes', 'test_ui_changes', 'request_review'],
    blockedActions: ['deploy_to_production', 'skip_local_testing'],
    reasonCodes: ['ui_refinement_pattern'],
    executionAuthority: false,
  },
  ratchetRecord: {
    recordVersion: 'v1',
    ratchetId: 'fixture-ratchet-id-duplicate',
    intentClass: 'ui_refinement',
    canonicalThreadId: 'fixture-thread-id-duplicate',
    canonicalThreadHint: 'UI refinement thread',
    status: 'proposed',
    prerequisiteRatchet: undefined,
    resumeTarget: 'ui_refinement',
    updates: [],
    supersedes: [],
    forksFrom: [],
    dependsOn: [],
    artifactRefs: ['artifacts/ui-refinement-v1.json'],
    decisionSummary: 'Implement UI refinement improvements',
    proofOnlySafeSummary: 'UI refinement ratchet proposed',
    privateNotesPointer: undefined,
    nextRecommendedRatchet: 'UI_REFINEMENT_V1',
    executionAuthority: false,
    createdAt: '2026-05-16T23:00:00.000Z',
    recordHash: 'fixture-record-hash-duplicate',
    isValid: true,
    validationReasonCodes: [],
  },
  lineageIndex: {
    indexVersion: 'v1',
    canonicalThreads: {
      'fixture-thread-id-duplicate': {
        threadId: 'fixture-thread-id-duplicate',
        threadHint: 'UI refinement thread',
        ratchetIds: ['fixture-ratchet-id-duplicate-1', 'fixture-ratchet-id-duplicate-2'],
        activeRatchetIds: ['fixture-ratchet-id-duplicate-1', 'fixture-ratchet-id-duplicate-2'],
        completedRatchetIds: [],
      },
    },
    activeRatchets: ['fixture-ratchet-id-duplicate-1', 'fixture-ratchet-id-duplicate-2'],
    blockedRatchets: [],
    supersededRatchets: [],
    archivedRatchets: [],
    dependencyEdges: [],
    resumeTargets: [],
    duplicateRiskWarnings: [
      {
        threadId: 'fixture-thread-id-duplicate',
        conflictingRatchetIds: ['fixture-ratchet-id-duplicate-1', 'fixture-ratchet-id-duplicate-2'],
        warning: 'Multiple active ratchets (fixture-ratchet-id-duplicate-1, fixture-ratchet-id-duplicate-2) targeting the same canonical thread',
      },
    ],
    indexHash: 'fixture-index-hash-duplicate',
  },
  nextStepProposal: {
    proposalVersion: 'v1',
    recommendedRatchet: 'UI_REFINEMENT_V1',
    canonicalThreadId: 'fixture-thread-id-duplicate',
    proposalReason: 'Continue with active ratchet',
    prerequisiteRequired: false,
    duplicateRiskWarnings: [
      'Multiple active ratchets targeting the same canonical thread',
    ],
    allowedNextActions: ['proceed_with_ratchet', 'review_progress'],
    blockedActions: [],
    executionAuthority: false,
    proposalHash: 'fixture-proposal-hash-duplicate',
  },
};

// Use imported live artifact index (build-time import from artifacts/governed-improvement-artifact-index-v1.json)
export const fixtureArtifactIndex: ArtifactIndex = LIVE_ARTIFACT_INDEX;

// Status badge data for improvement pane
export interface IndexStatusBadgeData {
  indexStatus: 'current' | 'stale' | 'invalid' | 'demo_fixture';
  artifactCount: number;
  indexHash: string;
  executionAuthority: false;
  generatedAt: string;
}

export const fixtureIndexStatusBadge: IndexStatusBadgeData = {
  indexStatus: 'current',
  artifactCount: LIVE_ARTIFACT_INDEX.artifactCount,
  indexHash: LIVE_ARTIFACT_INDEX.indexHash,
  executionAuthority: false,
  generatedAt: LIVE_ARTIFACT_INDEX.generatedAt,
};

export const fixtureIndexStatusBadgeStale: IndexStatusBadgeData = {
  indexStatus: 'stale',
  artifactCount: 5,
  indexHash: 'stale-hash-123',
  executionAuthority: false,
  generatedAt: '2026-05-15T00:00:00.000Z',
};

export const fixtureIndexStatusBadgeInvalid: IndexStatusBadgeData = {
  indexStatus: 'invalid',
  artifactCount: 0,
  indexHash: 'invalid-hash-456',
  executionAuthority: false,
  generatedAt: '2026-05-15T00:00:00.000Z',
};

export const fixtureIndexStatusBadgeDemo: IndexStatusBadgeData = {
  indexStatus: 'demo_fixture',
  artifactCount: 2,
  indexHash: 'demo-hash-789',
  executionAuthority: false,
  generatedAt: '2026-05-15T00:00:00.000Z',
};

export const fixtureArtifactIndexWithWarnings: ArtifactIndex = {
  indexVersion: 'v1',
  generatedAt: '2026-05-16T23:45:00.000Z',
  artifactCount: 2,
  artifacts: [
    {
      artifactPath: '/artifacts/closeout-test-v1.json',
      artifactHash: 'abc123',
      ratchetName: 'test-ratchet',
      canonicalThreadId: 'thread-duplicate',
      status: 'proposed',
      nextRecommendedRatchet: 'NEXT_RATCHET_V1',
    },
    {
      artifactPath: '/artifacts/closeout-test-v2.json',
      artifactHash: 'def456',
      ratchetName: 'another-test-ratchet',
      canonicalThreadId: 'thread-duplicate',
      status: 'proposed',
      nextRecommendedRatchet: 'NEXT_RATCHET_V2',
    },
  ],
  missingArtifacts: ['missing-artifact-1.json', 'missing-artifact-2.json'],
  duplicateCanonicalThreadWarnings: [
    'Thread thread-duplicate has multiple ratchets: test-ratchet, another-test-ratchet',
  ],
  executionAuthority: false,
  indexHash: 'test-index-hash-with-warnings',
};

// Canonical thread dashboard fixture data
export const fixtureCanonicalThreads: CanonicalThread[] = [
  {
    canonicalThreadId: 'thread-ui-refinement',
    canonicalThreadHint: 'UI refinement thread',
    status: 'active',
    activeRatchets: [
      {
        ratchetId: 'ratchet-ui-refinement-v1',
        ratchetName: 'UI_REFINEMENT_V1',
        status: 'proposed',
      },
    ],
    blockedRatchets: [],
    supersededRatchets: [],
    archivedRatchets: [],
    dependencies: [],
    resumeTargets: [],
    nextRecommendedRatchet: 'UI_REFINEMENT_V2',
    duplicateRiskWarnings: [],
    executionAuthority: false,
  },
  {
    canonicalThreadId: 'thread-deploy-hardening',
    canonicalThreadHint: 'Deploy hardening thread',
    status: 'blocked',
    activeRatchets: [],
    blockedRatchets: [
      {
        ratchetId: 'ratchet-deploy-hardening-v1',
        ratchetName: 'DEPLOY_HARDENING_V1',
        status: 'blocked',
      },
    ],
    supersededRatchets: [],
    archivedRatchets: [],
    dependencies: [
      {
        dependsOnRatchetId: 'LOCAL_DEV_HARDENING_V1',
        dependencyType: 'prerequisite',
        status: 'unsatisfied',
      },
    ],
    resumeTargets: [
      {
        blockedRatchetId: 'DEPLOY_HARDENING_V1',
        prerequisiteRatchetId: 'LOCAL_DEV_HARDENING_V1',
        resumeTarget: 'deploy_hardening',
      },
    ],
    nextRecommendedRatchet: 'LOCAL_DEV_HARDENING_V1',
    duplicateRiskWarnings: [],
    executionAuthority: false,
  },
  {
    canonicalThreadId: 'thread-auth-hardening',
    canonicalThreadHint: 'Auth hardening thread',
    status: 'superseded',
    activeRatchets: [],
    blockedRatchets: [],
    supersededRatchets: [
      {
        ratchetId: 'ratchet-auth-hardening-v1',
        ratchetName: 'AUTH_HARDENING_V1',
        status: 'superseded',
      },
    ],
    archivedRatchets: [],
    dependencies: [],
    resumeTargets: [],
    nextRecommendedRatchet: 'AUTH_HARDENING_V2',
    duplicateRiskWarnings: [],
    executionAuthority: false,
  },
  {
    canonicalThreadId: 'thread-public-surface-audit',
    canonicalThreadHint: 'Public surface audit thread',
    status: 'completed',
    activeRatchets: [],
    blockedRatchets: [],
    supersededRatchets: [],
    archivedRatchets: [
      {
        ratchetId: 'ratchet-public-surface-audit-v1',
        ratchetName: 'PUBLIC_SURFACE_AUDIT_V1',
        status: 'completed',
      },
    ],
    dependencies: [],
    resumeTargets: [],
    nextRecommendedRatchet: 'PUBLIC_SURFACE_AUDIT_V2',
    duplicateRiskWarnings: [],
    executionAuthority: false,
  },
];

// Thread state transition fixture data
export const fixtureThreadTransitions: ThreadStateTransition[] = [
  {
    transitionVersion: 'v1',
    fromState: 'active',
    toState: 'blocked',
    ratchetId: 'ratchet-fixture-1',
    isAllowed: true,
    reasonCodes: ['valid_transition'],
    requiredFields: [],
    executionAuthority: false,
    transitionHash: 'fixture-active-to-blocked-hash',
  },
  {
    transitionVersion: 'v1',
    fromState: 'active',
    toState: 'completed',
    ratchetId: 'ratchet-fixture-2',
    isAllowed: true,
    reasonCodes: ['valid_transition'],
    requiredFields: [],
    executionAuthority: false,
    transitionHash: 'fixture-active-to-completed-hash',
  },
  {
    transitionVersion: 'v1',
    fromState: 'blocked',
    toState: 'active',
    ratchetId: 'ratchet-fixture-3',
    isAllowed: true,
    reasonCodes: ['valid_transition'],
    requiredFields: [],
    executionAuthority: false,
    transitionHash: 'fixture-blocked-to-active-hash',
  },
  {
    transitionVersion: 'v1',
    fromState: 'archived',
    toState: 'active',
    ratchetId: 'ratchet-fixture-4',
    isAllowed: false,
    reasonCodes: ['invalid_transition'],
    requiredFields: [],
    executionAuthority: false,
    transitionHash: 'fixture-archived-to-active-hash',
  },
  {
    transitionVersion: 'v1',
    fromState: 'active',
    toState: 'blocked',
    ratchetId: 'ratchet-fixture-5',
    isAllowed: false,
    reasonCodes: ['valid_transition', 'missing_prerequisite_ratchet', 'missing_resume_target'],
    requiredFields: ['prerequisiteRatchet', 'resumeTarget'],
    executionAuthority: false,
    transitionHash: 'fixture-missing-prerequisite-hash',
  },
];

// Resume orchestration fixture data
export const fixtureResumeOrchestrations: ResumeOrchestration[] = [
  {
    orchestrationVersion: 'v1',
    canonicalThreadId: 'thread-resume-1',
    canResume: true,
    resumeTarget: 'deploy_hardening',
    prerequisiteSatisfied: true,
    unresolvedDependencies: [],
    recommendedNextRatchet: 'DEPLOY_HARDENING_V1',
    reasonCodes: ['prerequisite_completed', 'can_resume'],
    allowedNextActions: ['resume_work', 'review_progress'],
    blockedActions: [],
    executionAuthority: false,
    orchestrationHash: 'fixture-resume-completed-hash',
  },
  {
    orchestrationVersion: 'v1',
    canonicalThreadId: 'thread-resume-2',
    canResume: true,
    resumeTarget: 'deploy_hardening',
    prerequisiteSatisfied: true,
    unresolvedDependencies: [],
    recommendedNextRatchet: 'DEPLOY_HARDENING_V1',
    reasonCodes: ['prerequisite_superseded', 'can_resume'],
    allowedNextActions: ['resume_work'],
    blockedActions: [],
    executionAuthority: false,
    orchestrationHash: 'fixture-resume-superseded-hash',
  },
  {
    orchestrationVersion: 'v1',
    canonicalThreadId: 'thread-resume-3',
    canResume: false,
    resumeTarget: 'deploy_hardening',
    prerequisiteSatisfied: true,
    unresolvedDependencies: ['DEPENDENCY_V1'],
    recommendedNextRatchet: 'DEPLOY_HARDENING_V1',
    reasonCodes: ['unresolved_dependencies'],
    allowedNextActions: [],
    blockedActions: ['resume_without_prerequisite'],
    executionAuthority: false,
    orchestrationHash: 'fixture-resume-unresolved-hash',
  },
  {
    orchestrationVersion: 'v1',
    canonicalThreadId: 'thread-resume-4',
    canResume: false,
    prerequisiteSatisfied: false,
    unresolvedDependencies: [],
    reasonCodes: ['missing_resume_target'],
    allowedNextActions: [],
    blockedActions: [],
    executionAuthority: false,
    orchestrationHash: 'fixture-resume-missing-target-hash',
  },
];

// Canonical thread summary export fixture data
export const fixtureCanonicalThreadSummaryExport: CanonicalThreadSummaryExport = {
  exportVersion: 'v1',
  generatedAt: '2026-05-16T23:59:00.000Z',
  threadCount: 5,
  canonicalThreads: [],
  blockedThreadCount: 1,
  activeThreadCount: 1,
  completedThreadCount: 1,
  supersededThreadCount: 1,
  archivedThreadCount: 1,
  duplicateRiskWarnings: ['thread-4: duplicate_active_work'],
  unresolvedDependencyWarnings: ['thread-2: depends on LOCAL_DEV_HARDENING_V1'],
  artifactIndexHash: 'artifact-index-hash-abc123',
  proofOnly: true,
};

// Canonical ratchet index fixture data
export const fixtureCanonicalRatchetIndex = {
  indexVersion: 'v1',
  generatedAt: '2026-05-17T04:20:01.967Z',
  canonicalRatchets: 1,
  completedRatchets: 1,
  archivedRatchets: 1,
  activeRatchets: 0,
  supersededRatchets: 0,
  blockedRatchets: 0,
  latestCompletedArchive: {
    ratchetId: 'governed-improvement-public-verifier-release-archive',
    ratchetName: 'GOVERNED_IMPROVEMENT_PUBLIC_VERIFIER_RELEASE_ARCHIVE_V1',
    routePath: '/verify/improvement-summary',
    archivedStatus: 'completed',
    archiveIndexHash: '30d7aa5b697aeb053f81a1a156accc02dbb207a002432f1ee4268aaddb7ccf62',
    closeoutPath: 'docs/GOVERNED_IMPROVEMENT_PUBLIC_VERIFIER_RELEASE_ARCHIVE_CLOSEOUT.md',
  },
  ratchets: [
    {
      ratchetId: 'governed-improvement-public-verifier-release-archive',
      ratchetName: 'GOVERNED_IMPROVEMENT_PUBLIC_VERIFIER_RELEASE_ARCHIVE_V1',
      status: 'archived' as const,
      archivedAt: '2026-05-16T21:18:00Z',
      routePath: '/verify/improvement-summary',
      archiveIndexHash: '30d7aa5b697aeb053f81a1a156accc02dbb207a002432f1ee4268aaddb7ccf62',
      closeoutPath: 'docs/GOVERNED_IMPROVEMENT_PUBLIC_VERIFIER_RELEASE_ARCHIVE_CLOSEOUT.md',
      immutable: true,
    },
  ],
  executionAuthority: false as false,
  indexHash: 'dce67d10f76706cf6a618b347a1fa470a0fc57ca7cb65d39f37c520fa6a2cd9b',
};
